/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useRef, useEffect } from 'react'
import moment from 'moment'

export interface DatePickerProps {
  name: string
  onChange: (date: Date | null) => void
  selected?: Date | null
  popoverTargetOffset?: string
  todayButton?: string
  minDate?: Date
  maxDate?: Date
  dateFormatCalendar?: string
  dateFormat?: string
  showYearDropdown?: boolean
  className?: string
  id?: string
  placeholder?: string
  disabled?: boolean
}

const DatePicker: React.FC<DatePickerProps> = ({
  name,
  onChange,
  selected,
  className = 'form-control',
  id,
  placeholder,
  minDate,
  maxDate,
  disabled = false,
  todayButton
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [openUpward, setOpenUpward] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Update input value when selected date changes
  useEffect(() => {
    if (selected) {
      setInputValue(moment(selected).format('MMM DD, YYYY'))
    } else {
      setInputValue('')
    }
  }, [selected])

  // Check if calendar should open upward
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top

      // If less than 350px space below and more space above, open upward
      if (spaceBelow < 350 && spaceAbove > spaceBelow) {
        setOpenUpward(true)
      } else {
        setOpenUpward(false)
      }
    }
  }, [isOpen])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)

    // Try to parse the date
    const parsed = moment(value, ['MMM DD, YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'], true)
    if (parsed.isValid()) {
      const date = parsed.toDate()

      // Check min/max constraints
      if (minDate && date < minDate) return
      if (maxDate && date > maxDate) return

      onChange(date)
    } else if (value === '') {
      onChange(null)
    }
  }

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleDateSelect = (date: Date) => {
    onChange(date)
    setIsOpen(false)
  }

  const handleTodayClick = () => {
    const today = new Date()
    handleDateSelect(today)
  }

  // Generate calendar days
  const generateCalendar = () => {
    const currentDate = selected ? moment(selected) : moment()
    const startOfMonth = currentDate.clone().startOf('month')
    const endOfMonth = currentDate.clone().endOf('month')
    const startOfWeek = startOfMonth.clone().startOf('week')
    const endOfWeek = endOfMonth.clone().endOf('week')

    const days = []
    const day = startOfWeek.clone()

    while (day.isSameOrBefore(endOfWeek)) {
      days.push(day.clone())
      day.add(1, 'day')
    }

    return days
  }

  const renderCalendar = () => {
    if (!isOpen) return null

    const currentDate = selected ? moment(selected) : moment()
    const days = generateCalendar()
    const today = moment()

    return (
      <div
        className="datepicker-dropdown"
        style={{
          position: 'absolute',
          top: openUpward ? 'auto' : '100%',
          bottom: openUpward ? '100%' : 'auto',
          left: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '6px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          minWidth: '200px',
          marginTop: openUpward ? '0' : '2px',
          marginBottom: openUpward ? '2px' : '0',
          fontSize: '11px'
        }}
      >
        {/* Header with month/year navigation */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px'
          }}
        >
          <button
            type="button"
            onClick={() => {
              // Previous month logic would go here
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}
          >
            ‹
          </button>
          <span style={{ fontWeight: 'bold', fontSize: '11px' }}>{currentDate.format('MMMM YYYY')}</span>
          <button
            type="button"
            onClick={() => {
              // Next month logic would go here
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}
          >
            ›
          </button>
        </div>

        {/* Days of week header */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', marginBottom: '3px' }}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <div
              key={day}
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '10px',
                padding: '2px'
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
          {days.map((day, index) => {
            const isCurrentMonth = day.month() === currentDate.month()
            const isSelected = selected && day.isSame(moment(selected), 'day')
            const isToday = day.isSame(today, 'day')
            const isDisabled = (minDate && day.toDate() < minDate) || (maxDate && day.toDate() > maxDate)

            return (
              <button
                key={index}
                type="button"
                onClick={() => !isDisabled && handleDateSelect(day.toDate())}
                disabled={isDisabled}
                style={{
                  border: 'none',
                  padding: '4px',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  backgroundColor: isSelected ? '#007bff' : isToday ? '#f0f0f0' : 'transparent',
                  color: isSelected ? 'white' : isCurrentMonth ? 'black' : '#ccc',
                  opacity: isDisabled ? 0.5 : 1,
                  borderRadius: '2px',
                  fontSize: '10px',
                  minWidth: '20px',
                  minHeight: '20px'
                }}
              >
                {day.date()}
              </button>
            )
          })}
        </div>

        {/* Today button */}
        {todayButton && (
          <div style={{ marginTop: '6px', textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleTodayClick}
              style={{
                background: 'none',
                border: '1px solid #007bff',
                color: '#007bff',
                padding: '3px 8px',
                cursor: 'pointer',
                borderRadius: '3px',
                fontSize: '10px'
              }}
            >
              {todayButton}
            </button>
          </div>
        )}
      </div>
    )
  }

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        name={name}
        className={className}
        id={id}
        value={inputValue}
        onChange={handleInputChange}
        onClick={handleInputClick}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={false}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      />
      {renderCalendar()}
    </div>
  )
}

export default DatePicker
